import {Box, Radio, Stack, Text} from '@mantine/core';
import React from 'react';

import {OligoAwareFolderItemType, TagSchema} from 'constants/RegistryConstants';
import QueryingTagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import SearchInput from 'search/inputs/SearchInput';

import {EventTriggerConfig} from '../RoutineTypes';
import {withIndefiniteArticle} from '../routineUtils';
import {UNAVAILABLE_FEATURE_TOOLTIP} from '../triggerConstants';
import SelectableRadioCard from './SelectableRadioCard';
import {TargetScopeCapabilities, TriggerScope} from './types';

interface TargetScopeSectionProps {
  capabilities: TargetScopeCapabilities;
  config: Partial<EventTriggerConfig>;
  getCardClassName: (selected: boolean, disabled: boolean) => string;
  objectTypeLabel: string;
  pluralObjectTypeLabel: string;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  onScopeChange: (nextScope: TriggerScope) => void;
  scope: TriggerScope | undefined;
  searchInputContainerClassName: string;
  sectionClassName: string;
  selectedObjectSearchType: string;
  selectedObjectType: string;
  selectedSchemaFolderItemType: OligoAwareFolderItemType | null;
}

export default function TargetScopeSection({
  capabilities,
  config,
  getCardClassName,
  objectTypeLabel,
  pluralObjectTypeLabel,
  onChange,
  onScopeChange,
  scope,
  searchInputContainerClassName,
  sectionClassName,
  selectedObjectSearchType,
  selectedObjectType,
  selectedSchemaFolderItemType,
}: TargetScopeSectionProps): JSX.Element {
  const normalizedObjectTypeLabel = objectTypeLabel.toLowerCase();
  const objectCreatedSpecificInstanceTooltip = `Unable to select a specific ${normalizedObjectTypeLabel} with this type of trigger`;

  return (
    <Stack spacing={4} className={sectionClassName}>
      <Text size="sm" weight={600}>
        Targets
      </Text>
      <Text size="xs" color="dimmed">
        What is the scope for this routine
      </Text>

      <Radio.Group value={scope ?? ''}>
        <Stack spacing="sm">
          <SelectableRadioCard
            value="all"
            label={`All ${pluralObjectTypeLabel}`}
            description={`Run when any ${normalizedObjectTypeLabel} matches the specified trigger`}
            disabled={capabilities.scopeGroupDisabled}
            showTooltip={false}
            onSelect={() => {
              onScopeChange('all');
            }}
            cardClassName={getCardClassName(scope === 'all', capabilities.scopeGroupDisabled)}
          />
          <SelectableRadioCard
            value="schema"
            label={`A type of ${normalizedObjectTypeLabel}`}
            description={`Specify the specific type of ${normalizedObjectTypeLabel} that would trigger this routine`}
            disabled={capabilities.scopeGroupDisabled || capabilities.schemaScopeDisabled}
            showTooltip={capabilities.showSchemaScopeUnavailableTooltip}
            tooltipLabel={UNAVAILABLE_FEATURE_TOOLTIP}
            onSelect={() => {
              onScopeChange('schema');
            }}
            cardClassName={getCardClassName(
              scope === 'schema',
              capabilities.scopeGroupDisabled || capabilities.schemaScopeDisabled
            )}
          >
            {scope === 'schema' && selectedSchemaFolderItemType != null && (
              <Box className={searchInputContainerClassName}>
                <QueryingTagSchemaSelector
                  selectedTagSchemaId={config.schema_id ?? null}
                  onChange={(schema: TagSchema | null) => {
                    onChange({schema_id: schema?.id ?? undefined});
                  }}
                  oligoAwareFolderItemTypes={[selectedSchemaFolderItemType]}
                />
              </Box>
            )}
          </SelectableRadioCard>
          <SelectableRadioCard
            value="instance"
            label={`A specific ${normalizedObjectTypeLabel}`}
            description={`Select the specific instance of ${withIndefiniteArticle(normalizedObjectTypeLabel)} that would trigger this routine`}
            disabled={capabilities.scopeGroupDisabled || capabilities.instanceScopeDisabled}
            showTooltip={capabilities.showObjectCreatedInstanceTooltip}
            tooltipLabel={objectCreatedSpecificInstanceTooltip}
            onSelect={() => {
              onScopeChange('instance');
            }}
            cardClassName={getCardClassName(
              scope === 'instance',
              capabilities.scopeGroupDisabled || capabilities.instanceScopeDisabled
            )}
          >
            {scope === 'instance' && selectedObjectType !== '' && (
              <Box className={searchInputContainerClassName}>
                <SearchInput
                  types={[selectedObjectSearchType]}
                  selected={config.object_ids ?? []}
                  onSelect={(id) => {
                    if (id != null && !(config.object_ids ?? []).includes(id)) {
                      onChange({object_ids: [...(config.object_ids ?? []), id]});
                    }
                  }}
                  onRemove={(id) => {
                    onChange({
                      object_ids: (config.object_ids ?? []).filter((existingId) => existingId !== id),
                    });
                  }}
                  hideSelectedResults={true}
                  isMulti={true}
                  placeholder={`Search for ${objectTypeLabel}...`}
                  dropLeft={true}
                />
              </Box>
            )}
          </SelectableRadioCard>
        </Stack>
      </Radio.Group>
    </Stack>
  );
}
